// Widget "Alertas de completitud" del Dashboard (2026-09-10, pedido explícito
// del usuario) — a diferencia de IndiceCompletitudHc (admin/IndiceCompletitudHc.tsx,
// en /alertas: calidad-hc-sweep.ts, retrospectivo/agregado por
// muestreo del backend admin), este widget corre en el propio navegador del
// médico logueado, contra SUS PROPIOS Encounters de los últimos 30 días
// (participant=Practitioner/<yo>), reevaluando por cada uno las mismas 3
// reglas ya probadas de chequeoCompletitudConsulta.ts (evaluarCompletitudDeEncounter,
// con el Encounter ya resuelto acá — no repite el search). Sin botón de
// "marcar resuelto": un hallazgo desaparece solo cuando se completa el
// formulario real y este widget se vuelve a cargar (recarga de la página).
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Encounter } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TITULO_POR_ITEM } from '@/lib/pasosCalidadHc';
import { evaluarCompletitudDeEncounter, type HallazgoIncompleto } from '@/paciente/chequeoCompletitudConsulta';
import { claseTarjetaConPendientes, IndicadorPendiente } from './AlertaPendienteUI';
import { usePacientesPorId } from './FormulariosPendientes';

const DIAS_HACIA_ATRAS = 30;

/** `fecha` = inicio del Encounter — sin ella, 2+ consultas del mismo paciente con el mismo hallazgo se veían como filas idénticas (visto en vivo 2026-09-12). */
type Fila = { patientId: string; hallazgo: HallazgoIncompleto; fecha?: string };

function fechaCorta(iso: string | undefined): string {
  if (!iso) return '';
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime()) ? '' : fecha.toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
}

/**
 * `anamnesis-ingreso` es de una sola vez en la VIDA del paciente (ver
 * chequeoCompletitudConsulta.ts) — pero se evalúa por cada Encounter de la
 * ventana, así que un paciente con 2+ consultas recientes sin anamnesis
 * generaba 2+ filas idénticas ("Ana Torres — Anamnesis..." repetido).
 * Bug real encontrado en vivo 2026-09-10 al probar en producción. Se
 * deduplica DESPUÉS de armar las filas (no antes, en `evaluarCompletitudDeEncounter`,
 * que sigue evaluando por Encounter porque el widget del ADMIN sí necesita
 * saber de qué Encounter puntual viene cada hallazgo) quedándose con la
 * primera ocurrencia por paciente — los encounters ya llegan ordenados por
 * fecha descendente, así que "la primera" es la más reciente.
 */
function deduplicarAnamnesis(filas: Fila[]): Fila[] {
  const vistos = new Set<string>();
  return filas.filter((f) => {
    if (f.hallazgo.clave !== 'anamnesis-ingreso') return true;
    if (vistos.has(f.patientId)) return false;
    vistos.add(f.patientId);
    return true;
  });
}

/**
 * Mismo patrón de link que HistoriaTab.tsx/NotasTab.tsx (`?encounter=...`
 * armado con URLSearchParams). `anamnesis-ingreso` es la única excepción a
 * propósito: es un chequeo de una sola vez en la vida del paciente (ver
 * chequeoCompletitudConsulta.ts), no de ESTE encounter puntual, así que su
 * link no lleva query de encounter.
 */
function linkDe(patientId: string, hallazgo: HallazgoIncompleto): string {
  // `codigoFormulario` viene del Encounter real de la consulta (002 Consulta
  // Externa / 003 Hospitalización, ver chequeoCompletitudConsulta.ts) — antes
  // estaba fijo en 003, así que una alerta de consulta ambulatoria mandaba al
  // médico a un formulario fuera de su especialidad ("sin acceso"). Bug real
  // 2026-09-11.
  if (hallazgo.clave === 'anamnesis-ingreso') return `/${patientId}/formulario/${hallazgo.codigoFormulario ?? '002'}`;
  const codigo = hallazgo.clave === 'notas-evolucion' ? '005' : '006';
  const qs = new URLSearchParams({ encounter: hallazgo.encounterRef });
  return `/${patientId}/formulario/${codigo}?${qs.toString()}`;
}

export function AlertasCompletitudPropias(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  // Hook reactivo, no `medplum.getProfile()`: en una carga directa de la
  // página (el Dashboard es la pantalla de entrada tras el login) el perfil
  // todavía no llegó cuando corre el efecto — antes eso caía en "0 alertas"
  // y el efecto no volvía a correr nunca. Visto en vivo 2026-09-12: carga
  // directa = 0, navegación interna = 7. Sin perfil se queda en "Calculando…".
  const profileId = useMedplumProfile()?.id;
  const [filas, setFilas] = useState<Fila[] | null>(null);

  const cargar = useCallback(async () => {
    if (!profileId) return;
    const desde = new Date();
    desde.setDate(desde.getDate() - DIAS_HACIA_ATRAS);
    try {
      const encounters = await medplum.searchResources(
        'Encounter',
        `participant=${encodeURIComponent(`Practitioner/${profileId}`)}&date=ge${encodeURIComponent(desde.toISOString())}&_count=50&_sort=-date`,
      );
      // MT-042: con la regla de vínculo asistencial encendida, el servidor NO deja abrir a un paciente sin vínculo actual (una consulta propia ya cerrada, por ejemplo): sus
      // búsquedas darían 403 o, peor, 200 con 0 entradas (un falso «falta la nota de evolución»). Antes de evaluar a cada paciente se comprueba que se puede abrir; si no,
      // se omite (ni alertas ni falsos faltantes). Y un fallo de UN paciente nunca vacía la lista de los demás: `allSettled`.
      const porEncounter = await Promise.allSettled(
        (encounters as Encounter[]).map(async (encounter): Promise<Fila[]> => {
          const patientId = encounter.subject?.reference?.replace('Patient/', '');
          if (!patientId) return [];
          try {
            await medplum.readResource('Patient', patientId);
          } catch {
            return [];
          }
          const hallazgos = await evaluarCompletitudDeEncounter(medplum, patientId, encounter);
          const fecha = encounter.period?.start ?? encounter.meta?.lastUpdated;
          return hallazgos.map((hallazgo) => ({ patientId, hallazgo, fecha }));
        }),
      );
      setFilas(deduplicarAnamnesis(porEncounter.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))));
    } catch {
      setFilas([]);
    }
  }, [medplum, profileId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const lista = filas ?? [];
  const pacientes = usePacientesPorId(lista.map((f) => f.patientId));

  return (
    <div className={claseTarjetaConPendientes(lista.length > 0)}>
      <div className="flex items-center gap-1.5">
        {lista.length > 0 && <IndicadorPendiente />}
        <p className="text-sm font-medium text-hce-ink">
          {t('dashboard.alertasCompletitud.titulo')} ({lista.length})
        </p>
      </div>
      <p className="mt-0.5 text-xs text-hce-muted">{t('dashboard.alertasCompletitud.instruccion')}</p>

      {filas === null && <p className="mt-2 text-xs text-hce-muted">{t('dashboard.alertasCompletitud.cargando')}</p>}

      {filas !== null && lista.length === 0 && <p className="mt-2 text-xs text-hce-muted">{t('dashboard.alertasCompletitud.sinPendientes')}</p>}

      {filas !== null && lista.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {lista.map((fila, i) => (
            <li key={`${fila.patientId}-${fila.hallazgo.clave}-${fila.hallazgo.encounterRef}-${i}`} className="text-sm">
              <Link to={linkDe(fila.patientId, fila.hallazgo)} className="text-hce-pine underline-offset-2 hover:underline">
                {pacientes.get(fila.patientId) ?? fila.patientId} — {TITULO_POR_ITEM[fila.hallazgo.clave]}
              </Link>
              {fechaCorta(fila.fecha) && <span className="ml-1.5 text-xs text-hce-muted">· {fechaCorta(fila.fecha)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
